import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { Passenger } from './Passenger';

import type { DuplicatePassenger, MatchFields } from './management/merge/types';

export type FilterAllowSMSMessage = 'all' | 'yes' | 'no';

export interface IPassengerState {
  passengers: Passenger[];
  passengerSearchTerm: string;
  passengerMergeModalOpen: boolean;
  // merge modal
  selectedMatchFields: MatchFields[];
  selectedPassengers: DuplicatePassenger[];
  searchPassengerFirst: string;
  searchPassengerLast: string;
  parentPassenger: DuplicatePassenger | null;
  showDeletedPassengers: boolean;

  // Filters
  filterAllowSMSMessage: FilterAllowSMSMessage;
}

export const initialState: IPassengerState = {
  passengers: [],
  passengerSearchTerm: '',
  passengerMergeModalOpen: false,
  selectedMatchFields: [],
  selectedPassengers: [],
  searchPassengerFirst: '',
  searchPassengerLast: '',
  parentPassenger: null,
  showDeletedPassengers: false,
  filterAllowSMSMessage: 'all',
};

export const passengerSlice = createSlice({
  name: 'passenger',
  initialState,
  reducers: {
    setPassengers: (state, action: { payload: Passenger[]; type: string }) => {
      state.passengers = action.payload;
    },
    setPassengerSearchTerm: (state, action: PayloadAction<string>) => {
      state.passengerSearchTerm = action.payload;
    },
    setPassengerMergeModalOpen: (state, action: PayloadAction<boolean>) => {
      state.passengerMergeModalOpen = action.payload;
    },
    setSearchPassengerFirst: (state, action: PayloadAction<string>) => {
      state.searchPassengerFirst = action.payload;
    },
    setSearchPassengerLast: (state, action: PayloadAction<string>) => {
      state.searchPassengerLast = action.payload;
    },
    setSelectedMatchFields: (state, action: PayloadAction<MatchFields[]>) => {
      state.selectedMatchFields = action.payload;
    },
    setDuplicatePassengers: (
      state,
      action: PayloadAction<DuplicatePassenger[]>
    ) => {
      state.selectedPassengers = action.payload;
    },
    setParentPassenger: (state, action: PayloadAction<DuplicatePassenger>) => {
      state.parentPassenger = action.payload;
    },
    setShowDeletedPassengers: (state, action: PayloadAction<boolean>) => {
      state.showDeletedPassengers = action.payload;
    },
    resetMergeWizard: (state) => {
      state.passengerMergeModalOpen = false;
      state.parentPassenger = null;
      state.searchPassengerFirst = '';
      state.searchPassengerLast = '';
      state.selectedPassengers = [];
      state.selectedMatchFields = [];
    },
    setFilterAllowSMSMessage: (
      state,
      action: PayloadAction<FilterAllowSMSMessage>
    ) => {
      state.filterAllowSMSMessage = action.payload;
    },
    clearFilters: (state) => {
      state.filterAllowSMSMessage = 'all';
    },
  },
  selectors: {
    hasFilters: (state) => {
      return state.filterAllowSMSMessage !== 'all';
    },
  },
});

export const {
  setPassengers,
  setPassengerSearchTerm,
  // Passenger merge modal
  setPassengerMergeModalOpen,
  setSearchPassengerFirst,
  setSearchPassengerLast,
  setSelectedMatchFields,
  setDuplicatePassengers,
  setParentPassenger,
  setShowDeletedPassengers,
  resetMergeWizard,
  setFilterAllowSMSMessage,
  clearFilters,
} = passengerSlice.actions;

export const passengerSelectors = passengerSlice.selectors;
